import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, ShieldCheck } from "lucide-react";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const STATUSES = [
    { value: "", label: "All" },
    { value: "paid", label: "Paid" },
    { value: "packed", label: "Packed" },
    { value: "dispatched", label: "Dispatched" },
    { value: "delivered", label: "Delivered" },
    { value: "cancelled", label: "Cancelled" },
];
const STATUS_PILL = {
    created: "bg-slate-100 text-slate-600",
    payment_pending: "bg-amber-100 text-amber-700",
    paid: "bg-emerald-100 text-emerald-700",
    packed: "bg-blue-100 text-blue-700",
    dispatched: "bg-indigo-100 text-indigo-700",
    delivered: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-rose-100 text-rose-600",
};

const Content = () => {
    const [params, setParams] = useSearchParams();
    const [status, setStatus] = useState(params.get("status") || "");
    const [q, setQ] = useState("");
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/orders", {
                params: { status: status || undefined, q: q.trim() || undefined, page, page_size: 25 },
            });
            setRows(r.data.items);
            setTotal(r.data.total);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, page]);

    return (
        <div className="space-y-5" data-testid="admin-orders-page">
            <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
                    {STATUSES.map((s) => (
                        <button key={s.value} onClick={() => { setStatus(s.value); setPage(1); const n = new URLSearchParams(params); if (s.value) n.set("status", s.value); else n.delete("status"); setParams(n, { replace: true }); }}
                            className={`rounded-md px-3 py-1.5 text-xs font-medium ${status === s.value ? "bg-brand-700 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                            data-testid={`orders-status-${s.value || "all"}`}>
                            {s.label}
                        </button>
                    ))}
                </div>
                <form onSubmit={(e) => { e.preventDefault(); setPage(1); load(); }} className="relative ml-auto flex-1 max-w-md">
                    <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by order # or customer..." className="input-clean !rounded-lg pl-10" data-testid="orders-search" />
                </form>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-soft">
                <table className="min-w-full divide-y divide-slate-100 text-sm">
                    <thead className="bg-slate-50/80 text-[10px] uppercase tracking-wider text-slate-500">
                        <tr>
                            <th className="px-4 py-3 text-left">Order</th>
                            <th className="px-4 py-3 text-left">Customer</th>
                            <th className="px-4 py-3 text-right">Items</th>
                            <th className="px-4 py-3 text-right">Total</th>
                            <th className="px-4 py-3 text-left">Status</th>
                            <th className="px-4 py-3 text-left">Placed</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? [0,1,2,3,4].map((i) => <tr key={i}><td colSpan={6} className="px-4 py-3"><div className="h-6 skeleton rounded" /></td></tr>) :
                          rows.length === 0 ? <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No orders match.</td></tr> :
                          rows.map((o) => (
                            <tr key={o.id} className="cursor-pointer hover:bg-slate-50" data-testid={`order-row-${o.id}`}>
                                <td className="px-4 py-3">
                                    <Link to={`/admin/orders/${o.id}`} className="font-medium text-slate-900 hover:text-brand-700">{o.order_no}</Link>
                                    {o.rx_required && <span className="ml-1.5 inline-flex items-center gap-0.5 rounded bg-brand-50 px-1 py-0.5 text-[9px] font-semibold text-brand-700"><ShieldCheck className="h-2.5 w-2.5" /> Rx</span>}
                                </td>
                                <td className="px-4 py-3">
                                    <p className="text-slate-900">{o.customer_name}</p>
                                    <p className="text-xs text-slate-500">{o.customer_email}</p>
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums text-slate-700">{o.total_items}</td>
                                <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">{inr(o.total)}</td>
                                <td className="px-4 py-3">
                                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_PILL[o.status] || "bg-slate-100"}`}>{o.status}</span>
                                </td>
                                <td className="px-4 py-3 text-xs text-slate-500">{new Date(o.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                            </tr>
                          ))}
                    </tbody>
                </table>
                {!loading && total > 25 && (
                    <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
                        <span>Page {page} · {Math.min(page * 25, total)} of {total}</span>
                        <div className="flex gap-2">
                            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="rounded-md border border-slate-200 px-3 py-1 disabled:opacity-50">Prev</button>
                            <button disabled={page * 25 >= total} onClick={() => setPage(p => p + 1)} className="rounded-md border border-slate-200 px-3 py-1 disabled:opacity-50">Next</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const AdminOrders = () => (
    <AdminAuthGuard>
        <AdminLayout title="Orders" subtitle="Pack, dispatch, deliver and refund">
            <Content />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminOrders;
